import { FC, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Accordion, { AccordionItem } from '@ingka/accordion';

import { useTrackContent } from '@/lib/hooks/tracking';
import { TFunction } from 'i18next';

type FaqItem = {
  title: string;
  description: string;
  key: string;
};

const useFaqTracking = () => {
  const [trackContent] = useTrackContent();

  useEffect(() => {
    trackContent({ content: 'faq', type: 'accordion' }).catch(() => {});
  }, [trackContent]);
};

const prepareFaq = (t: TFunction): FaqItem[] => {
  const items = t('page.faq.accordion', { returnObjects: true });
  return Object.entries(items)
    .map(([key]) => ({
      key,
      title: `page.faq.accordion.${key}.title`,
      description: `page.faq.accordion.${key}.description`,
    }))
    .filter(
      ({ title, description }) =>
        title !== t(title) && description !== t(description)
    ); // Filter incomplete translations
};

export const Faq: FC = () => {
  const { t } = useTranslation();
  const items = prepareFaq(t);

  useFaqTracking();

  return (
    <>
      <div data-testid="faq">
        <h1 className="font-bold text-2xl leading-loose">
          {t('page.faq.title')}
        </h1>
      </div>

      <div className="mt-8">
        <Accordion size="large">
          {items.map(({ key, title, description }) => {
            return (
              <AccordionItem
                id={`accordion-item_${key}`}
                key={key}
                title={<p className="px-6">{t(title)}</p>}
              >
                <p className="text-sm leading-snug px-6">
                  <Trans i18nKey={description} />
                </p>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </>
  );
};
